import { type FC, useRef, useState } from 'react';

import Typography from 'components/Typography';

import { useOutsideClickHandle } from '../../hooks/useOutsideClick';
import {
  SelectArrow, SelectContainer, SelectHeader, SelectOption, SelectOptionsList, SelectWrapper,
} from './styled';
import { type SelectOption as SelectOptionType, type SelectProps } from './types';

const Select: FC<SelectProps> = ({
  values,
  onSelect,
  defaultValueIndex,
  label,
  closeOnOutsideClick,
  name,
  popupVersion,
  'data-test-id': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentValueIndex, setCurrentValueIndex] = useState(defaultValueIndex || 0);
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClickHandle(ref, () => {
    if (isOpen && closeOnOutsideClick) setIsOpen(false);
  });

  const setSelectValue = (newValue: SelectOptionType, i: number) => {
    setIsOpen(false);
    setCurrentValueIndex(i);
    if (name) {
      onSelect(newValue, i, name);
    } else {
      onSelect(newValue, i, '');
    }
  };

  const defaultValue = values[currentValueIndex];
  const defaultValueLabel = typeof defaultValueIndex === 'number' && typeof defaultValue === 'string'
    ? values[defaultValueIndex]
    : typeof defaultValue !== 'string' && defaultValue.label;

  return (
    <SelectWrapper tabIndex={0} ref={ref} popUpVersion={popupVersion} data-test-id={dataTestId}>
      {label && (
        <Typography font="secondary" lSpacing={1} textColor="textWhite" onClick={() => setIsOpen(!isOpen)} data-testid="label">
          {label}
        </Typography>
      )}
      <SelectContainer onClick={() => setIsOpen(!isOpen)} data-testid="select">
        <SelectHeader>
          <Typography size="title">{defaultValueLabel}</Typography>
        </SelectHeader>
        <SelectArrow />
        <SelectOptionsList isOpen={isOpen} data-testid="optionsList">
          {values.map((value, i) => {
            const optionLabel = typeof value === 'string' ? value : value.label;
            return (
              <SelectOption onClick={() => setSelectValue(value, i)} key={optionLabel} data-test-id={`${dataTestId}_option_${i}`}>
                {optionLabel}
              </SelectOption>
            );
          })}
        </SelectOptionsList>
      </SelectContainer>
    </SelectWrapper>
  );
};

export default Select;
